/* Draft Expanse v19 W3 spa package group integrity. Prevents orphan add-ons. */
(() => {
  'use strict';
  if (window.__ycW3GroupIntegrityInstalled) return;
  window.__ycW3GroupIntegrityInstalled = true;
  const base = () => window.Shopify?.routes?.root || '/';
  let busy = false, checkingOut = false, scheduled = false;
  const keyOf = item => String(item?.properties?._options_group_id ?? '');
  const isChild = item => Boolean(keyOf(item) && item?.properties?._options_hidden != null && String(item.properties._options_hidden) !== '');
  const groups = cart => {
    const grouped = new Map();
    for (const item of cart.items) {
      const groupId = keyOf(item);
      if (!groupId) continue;
      if (!grouped.has(groupId)) grouped.set(groupId, { parent: null, children: [] });
      const group = grouped.get(groupId);
      if (isChild(item)) group.children.push(item);
      else if (!group.parent) group.parent = item;
    }
    return grouped;
  };
  async function request(path, payload) {
    const response = await fetch(base() + path, {
      credentials: 'same-origin', cache: 'no-store',
      ...(payload ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) } : {})
    });
    let data;
    try { data = await response.json(); } catch (_) {}
    if (!response.ok) throw new Error(data?.description || data?.message || 'Unable to update this spa package.');
    return data;
  }
  const cart = () => request('cart.js');
  const refresh = () => window.location.assign(base() + 'cart');
  const buttonBusy = (button, value) => { if (button) button.disabled = value; };
  async function editGroup(groupId, parentKey, quantity, button) {
    if (busy) return;
    busy = true; buttonBusy(button, true);
    try {
      const before = await cart();
      const group = groups(before).get(groupId);
      if (!group?.parent || group.parent.key !== parentKey) throw new Error('The package changed. Reload your cart.');
      if (quantity === 0) {
        // Remove paid add-ons before their parent, even if a subsequent request fails.
        for (const item of group.children) await request('cart/change.js', { id: item.key, quantity: 0 });
        await request('cart/change.js', { id: group.parent.key, quantity: 0 });
      } else {
        const updates = { [group.parent.key]: quantity };
        for (const item of group.children) updates[item.key] = quantity;
        await request('cart/update.js', { updates });
      }
      const after = await cart(), result = groups(after).get(groupId);
      if (quantity === 0 && result) throw new Error('The package was not fully removed; please review your cart.');
      if (quantity !== 0 && (!result?.parent || result.parent.quantity !== quantity || result.children.some(c => c.quantity !== quantity))) {
        throw new Error('Some enhancement quantities were not updated. Please review your cart.');
      }
      refresh();
    } catch (error) {
      window.alert(error?.message || 'Unable to update the package. Please try again.');
      busy = false; buttonBusy(button, false);
    }
  }
  async function removeAddon(groupId, parentKey, childKey, button) {
    if (busy) return;
    busy = true; buttonBusy(button, true);
    try {
      const before = await cart(), group = groups(before).get(groupId);
      if (!group?.parent || group.parent.key !== parentKey) throw new Error('The package changed. Please reload your cart.');
      const child = group.children.find(item => item.key === childKey);
      if (!child) throw new Error('That enhancement is no longer in your cart.');
      await request('cart/change.js', { id: child.key, quantity: 0 });
      const after = await cart(), updatedGroup = groups(after).get(groupId);
      if (!updatedGroup?.parent || updatedGroup.parent.key !== parentKey) throw new Error('The package changed unexpectedly. Review your cart.');
      if (updatedGroup.children.some(item => item.key === childKey)) throw new Error('Could not remove that enhancement. Please try again.');
      // Customer remains on the cart; only this paid child is removed.
      refresh();
    } catch (error) {
      window.alert(error?.message || 'Unable to remove this enhancement.');
      busy = false; buttonBusy(button, false);
    }
  }
  // Capture quantity events before Expanse changes the W3 parent alone.
  document.addEventListener('cart:quantity', event => {
    const [key, rawQty, control] = event.detail || [];
    const parent = control?.closest?.('[data-yc-w3-parent]');
    if (!parent || key !== parent.dataset.key) return;
    const qty = Number(rawQty);
    event.stopImmediatePropagation();
    if (!Number.isInteger(qty) || qty < 0) return;
    editGroup(parent.dataset.ycW3Parent, parent.dataset.key, qty, control);
  }, true);
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-yc-remove-w3-addon]');
    if (!button) return;
    event.preventDefault(); event.stopImmediatePropagation();
    removeAddon(button.dataset.ycGroupId, button.dataset.ycParentKey, button.dataset.ycAddonKey, button);
  }, true);
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-yc-remove-w3-group]');
    if (!button) return;
    event.preventDefault(); event.stopImmediatePropagation();
    editGroup(button.dataset.ycGroupId, button.dataset.ycParentKey, 0, button);
  }, true);
  function neededUpdates(data) {
    const updates = {};
    for (const group of groups(data).values()) {
      if (!group.parent) {
        for (const orphan of group.children) updates[orphan.key] = 0;
      } else {
        for (const child of group.children) {
          if (child.quantity !== group.parent.quantity) updates[child.key] = group.parent.quantity;
        }
      }
    }
    return updates;
  }
  async function heal() {
    if (busy) return false;
    busy = true;
    try {
      const before = await cart(), updates = neededUpdates(before);
      if (!Object.keys(updates).length) return false;
      await request('cart/update.js', { updates });
      const after = await cart();
      if (Object.keys(neededUpdates(after)).length) throw new Error('Cart enhancements could not be reconciled.');
      refresh();
      return true;
    } catch (error) {
      window.alert(error?.message || 'Please review your spa package enhancements before checkout.');
      return true;
    } finally {
      busy = false;
    }
  }
  // Ensure a late W3/third-party cart update cannot leave orphan lines in the cart.
  function scheduleHeal() {
    if (scheduled || busy || !document.querySelector('.cart__items')) return;
    const cartPath = base().replace(/\/$/, '') + '/cart';
    const onCartPage = window.location.pathname === cartPath || window.location.pathname.startsWith(cartPath + '/');
    const drawerOpen = document.documentElement.classList.contains('js-drawer-open') || document.documentElement.classList.contains('cart-open');
    if (!onCartPage && !drawerOpen) return;
    scheduled = true;
    setTimeout(async () => {
      scheduled = false;
      await heal();
    }, 250);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleHeal, { once: true });
  else scheduleHeal();
  window.addEventListener('pageshow', event => { if (event.persisted) scheduleHeal(); });
  document.addEventListener('cart:updated', scheduleHeal);
  document.addEventListener('cart:build', scheduleHeal);
  // Final safety gate: an orphan or mismatched W3 quantity cannot proceed to checkout.
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!form?.matches?.('#CartPageForm, .cart__drawer-form')) return;
    if (checkingOut || !form.querySelector('[name="checkout"]')) return;
    if (!document.querySelector('[data-yc-w3-parent], .cart__item[data-yc-w3-orphan]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;
    const changed = await heal();
    if (changed) return;
    checkingOut = true;
    try {
      form.requestSubmit(event.submitter || form.querySelector('[name="checkout"]'));
    } finally {
      checkingOut = false;
    }
  }, true);
})();

(() => {
  'use strict';
  const selection = new URLSearchParams(location.search).get('options');
  if (!selection || !location.pathname.includes('/products/')) return;
  let form, root, fields, groupId, status, ready, pending = Promise.resolve(), failed = false, started = false, updateButton = null, saving = false;
  const base = () => window.Shopify?.routes?.root || '/';
  const child = item => Boolean(item.properties?._options_hidden);
  async function request(path, data) {
    const response = await fetch(base() + path, {
      credentials: 'same-origin', cache: 'no-store',
      ...(data ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(data) } : {})
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Unable to save your enhancements.');
    return result;
  }
  function configuration() {
    return new Promise((resolve, reject) => {
      const callback = '__ycW3EditConfig' + Date.now();
      const script = document.createElement('script');
      const timer = setTimeout(() => finish(new Error('Enhancements could not load. Please reload this page.')), 15000);
      function finish(error, result) {
        clearTimeout(timer);
        script.remove();
        delete window[callback];
        error ? reject(error) : resolve(result);
      }
      window[callback] = result => {
        const list = Array.isArray(result?.fields) ? result.fields
          : Array.isArray(result) ? result : null;
        if (!list) return finish(new Error('W3 did not return a valid enhancement configuration. Please reload this page.'));
        finish(null, list);
      };
      script.onerror = () => finish(new Error('Enhancements could not load. Please reload this page.'));
      const url = new URL('https://productoptions.w3apps.co/api/productwidget');
      url.search = new URLSearchParams({ shopName: window.Shopify.shop, productId: root.dataset.productId, tags: root.dataset.tags || '', callback }).toString();
      script.src = url.href;
      document.head.append(script);
    });
  }
  function controls(field) {
    return [...root.querySelectorAll('[data-field-id]')].find(el => String(el.dataset.fieldId) === String(field.id));
  }
  function options() {
    const result = [];
    for (const field of fields) {
      // W3 groups can contain headings, notes and other field types. They are
      // not paid checkbox upgrades and must not disable the bag editor.
      if (String(field?.type || '').toLowerCase() !== 'checkbox') continue;
      for (const rule of (Array.isArray(field.logic) ? field.logic : [])) {
        const actions = Array.isArray(rule.actions) ? rule.actions : [];
        const linked = actions.filter(action => String(action?.type || '').toLowerCase() === 'product');
        if (!linked.length) continue;
        const conditions = Array.isArray(rule.rules) ? rule.rules : [];
        if (conditions.length !== 1 || !['==', 'equals', 'equal'].includes(String(conditions[0].operator).toLowerCase()))
          throw new Error('A W3 enhancement rule has changed. Please contact the spa before editing this package.');
        for (const action of linked) {
          if (!action.selectedProduct?.variantId || action.actionId == null)
            throw new Error('A W3 enhancement product link is incomplete. Please contact the spa.');
          result.push({ field, value: String(conditions[0].value), action: String(action.actionId), ...action.selectedProduct });
        }
      }
    }
    if (!result.length && root.querySelector('input[type="checkbox"]'))
      throw new Error('W3 has not supplied the linked upgrade products. Your bag has not been changed.');
    return result;
  }
  function show(message, error = false) {
    if (!status) return;
    status.textContent = message;
    status.setAttribute('role', error ? 'alert' : 'status');
    status.style.color = error ? '#ad2525' : '';
    if (error) console.warn('[Yellow Creek spa enhancements]', message);
  }
  function display(cart, parent) {
    const extras = cart.items.filter(item => child(item) && item.properties._options_group_id === groupId)
      .reduce((sum, item) => sum + item.final_line_price, 0);
    const panel = form.querySelector('[data-yc-zen-price-breakdown]');
    const money = amount => '$' + (amount / 100).toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (panel) {
      panel.querySelector('[data-yc-zen-extras-row]').hidden = extras === 0;
      panel.querySelector('[data-yc-zen-total-row]').hidden = extras === 0;
      panel.querySelector('[data-yc-zen-extras]').textContent = '+' + money(extras);
      panel.querySelector('[data-yc-zen-total]').textContent = money(parent.final_line_price + extras);
    }
    const summary = root.querySelector('.pricing-info-total-price');
    if (summary) summary.textContent = money(extras);
  }
  function findParent(cart) {
    return cart.items.find(item => !child(item) && String(item.properties?._options_group_id) === groupId);
  }
  const normalizeId = value => String(value ?? '').split('/').pop();
  const sameEditToken = item => {
    try {
      return new URL(item.properties?._options_edit_url || '', location.origin)
        .searchParams.get('options') === selection;
    } catch (_) { return false; }
  };
  function resolveOriginal(cart) {
    const items = cart.items.filter(item => !child(item) && item.properties?._options_group_id);
    const exact = items.filter(sameEditToken);
    if (exact.length === 1) return exact[0];
    // Some W3 versions regenerate the edit snapshot URL. In that case only
    // fall back when precisely ONE matching product exists in the bag.
    const matchingProduct = items.filter(item =>
      normalizeId(item.product_id) === normalizeId(root.dataset.productId));
    if (matchingProduct.length === 1) return matchingProduct[0];
    if (matchingProduct.length > 1)
      throw new Error('More than one of this package is in your bag. Please use the specific Edit enhancements link from your bag.');
    throw new Error('This package is no longer in your bag. Please return to your bag.');
  }
  // W3's edit link is only a saved snapshot. Reconcile the existing cart group,
  // not a second product submission. Each operation uses a freshly read item key.
  async function sync() {
    let current = await request('cart.js');
    let parent = findParent(current);
    if (!parent) throw new Error('This package is no longer in your bag. Return to the bag to review it.');
    const selected = options().filter(option => {
      const control = controls(option.field);
      if (!control) throw new Error('One of the enhancement controls could not be found. Please reload.');
      return [...control.querySelectorAll('input[type="checkbox"]')]
        .some(input => input.checked && input.value === option.value);
    });
    const isMatching = (item, option) =>
      String(item.properties?._options_action) === String(option.action) &&
      normalizeId(item.variant_id) === normalizeId(option.variantId);
    const groupChildren = data => data.items.filter(item =>
      child(item) && String(item.properties?._options_group_id) === groupId
    );
    // First remove deselected lines; do not use stale keys or a bulk update,
    // because Shopify can change line keys after each cart mutation.
    for (const previous of groupChildren(current)) {
      if (selected.some(option => isMatching(previous, option))) continue;
      current = await request('cart.js');
      const latest = groupChildren(current).find(item =>
        String(item.properties?._options_action) === String(previous.properties?._options_action) &&
        normalizeId(item.variant_id) === normalizeId(previous.variant_id)
      );
      if (!latest) continue;
      await request('cart/change.js', { id: latest.key, quantity: 0 });
      current = await request('cart.js');
      if (groupChildren(current).some(item => isMatching(item, {
        action: previous.properties?._options_action, variantId: previous.variant_id
      }))) throw new Error('A removed enhancement is still in the bag. Please try again.');
    }
    // Then reconcile the remaining options, independently and at parent quantity.
    for (const option of selected) {
      current = await request('cart.js');
      parent = findParent(current);
      if (!parent) throw new Error('The spa package changed while editing. Please review your bag.');
      const match = groupChildren(current).find(item => isMatching(item, option));
      if (!match) {
        await request('cart/add.js', { items: [{
          id: normalizeId(option.variantId), quantity: parent.quantity,
          properties: {
            _options_group_id: groupId,
            _options_hidden: 'true',
            _options_action: String(option.action)
          }
        }] });
      } else if (match.quantity !== parent.quantity) {
        await request('cart/change.js', { id: match.key, quantity: parent.quantity });
      }
    }
    // Refresh the parent key after child changes, then update W3's stored
    // selection metadata without replacing the parent or creating a duplicate.
    current = await request('cart.js');
    parent = findParent(current);
    if (!parent) throw new Error('The original package was not found. Please review your bag.');
    const properties = { ...parent.properties };
    for (const field of fields) {
      if (String(field?.type || '').toLowerCase() !== 'checkbox') continue;
      const control = controls(field);
      if (!control) throw new Error('Enhancement fields changed. Please reload this page.');
      const chosen = [...control.querySelectorAll('input[type="checkbox"]')]
        .filter(input => input.checked).map(input => input.value);
      if (field.name) properties[field.name] = chosen.join(', ');
    }
    properties._options_addon_products = JSON.stringify(selected.map(option => ({
      id: option.id, variantId: option.variantId,
      handle: option.handle, actionID: option.action
    })));
    for (const input of form.querySelectorAll('[data-yc-package-gift-input]')) {
      const name = input.name.match(/^properties\[(.*)\]$/)?.[1];
      if (name) properties[name] = input.disabled ? '' : input.value;
    }
    await request('cart/change.js', {
      id: parent.key, quantity: parent.quantity, properties
    });
    const verified = await request('cart.js');
    const saved = findParent(verified), savedChildren = groupChildren(verified);
    if (!saved || savedChildren.length !== selected.length ||
        savedChildren.some(item =>
          !selected.some(option => isMatching(item, option)) ||
          item.quantity !== saved.quantity
        ) || selected.some(option => !savedChildren.some(item => isMatching(item, option))))
      throw new Error('Some enhancements were not saved correctly. Please review your bag.');
    display(verified, saved);
  }
  function save() {
    if (saving) return pending;
    saving = true;
    failed = false;
    if (updateButton) {
      updateButton.disabled = true;
      updateButton.setAttribute('aria-busy', 'true');
    }
    show('Updating your bag…');
    pending = Promise.resolve(ready).then(sync).then(() => {
      failed = false;
      show('Your enhancements have been updated.');
    }).catch(error => {
      failed = true;
      show(error?.message || 'Unable to update your bag. Please try again.', true);
    }).finally(() => {
      saving = false;
      if (updateButton) {
        updateButton.disabled = false;
        updateButton.removeAttribute('aria-busy');
      }
    });
    return pending;
  }
  async function returnToBag(event) {
    if (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    if (saving) return;
    await save();
    if (!failed) location.assign(base() + 'cart');
  }
  function init() {
    root = document.querySelector('[data-yc-spa-package-w3] .w3-product-options');
    if (started || !root?.querySelector('input[type="checkbox"]')) return;
    started = true;
    form = root.closest('form');
    updateButton = form.querySelector('button[name="add"], button[type="submit"]');
    if (!updateButton) { started = false; return; }
    // W3's normal submit re-adds the package. This explicit edit action must
    // update the existing group instead, without a duplicate or page reload.
    updateButton.type = 'button';
    const text = updateButton.querySelector('span') || updateButton;
    if (text) text.textContent = 'Update bag';
    status = document.createElement('p');
    status.setAttribute('aria-live', 'assertive');
    status.style.cssText = 'font:inherit;font-size:13px;margin:8px 0;line-height:1.45';
    updateButton.after(status);
    show('Loading your current enhancements…');
    ready = Promise.all([request('cart.js'), configuration()]).then(([cart, config]) => {
      if (!Array.isArray(config)) throw new Error('W3 did not return a valid list of enhancement fields. Please reload.');
      fields = config;
      const parent = resolveOriginal(cart);
      groupId = String(parent.properties._options_group_id);
      const existing = cart.items.filter(item => child(item) && String(item.properties._options_group_id) === groupId);
      for (const option of options()) {
        const fieldControl = controls(option.field);
        if (!fieldControl) throw new Error('A W3 enhancement field is missing. Please reload the page.');
        const input = [...fieldControl.querySelectorAll('input[type="checkbox"]')].find(input => input.value === option.value);
        if (!input) throw new Error('Enhancement options changed. Please reload the page.');
        input.checked = existing.some(item => String(item.properties._options_action) === String(option.action));
      }
      display(cart, parent);
      show('Choose your enhancements, then select Update bag to save them.');
    }).catch(error => { failed = true; show(error.message, true); throw error; });
    ready.catch(() => {});
    // Do not automatically submit cart mutations on every checkbox change:
    // W3 itself may rebuild input nodes while the customer is choosing extras.
    document.addEventListener('change', event => {
      if (form.contains(event.target) &&
          event.target.matches('input[type="checkbox"], [data-yc-package-gift-input], [data-yc-package-gift-toggle]'))
        show('You have unsaved changes. Select Update bag to apply them.');
    });
    // Capture on document before W3 or Expanse's native add-to-cart handler.
    // A real button element with type=button eliminates accidental re-add.
    document.addEventListener('click', event => {
      if (!updateButton?.contains(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      void returnToBag();
    }, true);
    document.addEventListener('submit', event => {
      if (event.target !== form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      void returnToBag();
    }, true);
  }
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('productOptionsLoadedJS', init);
  document.body.addEventListener('productOptionsLoadedJS', init);
  // W3 loads options asynchronously and its event may not bubble to document.
  const shell = document.querySelector('[data-yc-spa-package-w3]');
  if (shell) {
    const observer = new MutationObserver(() => {
      init();
      if (started) observer.disconnect();
    });
    observer.observe(shell, { childList: true, subtree: true });
  }
  init();
})();